import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Sparkline } from '@/components/Sparkline'
import {
  getDashboard,
  getFxRates,
  getInventoryPositions,
  getMarketHistory,
  getMarketSpot,
  type DashboardOut,
  type FxRateOut,
  type HistoryPointOut,
  type PositionOut,
  type SpotQuoteOut,
} from '@/lib/api'
import { intlLocale, t } from '@/i18n'
import { useLocale } from '@/i18n/LocaleProvider'
import { formatMoney, formatQty, formatRate } from '@/lib/format'

const FIAT = new Set(['PLN', 'UAH', 'USD', 'EUR'])
const CORE_FIAT = new Set(['USD/PLN', 'USD/UAH', 'EUR/PLN', 'EUR/UAH', 'EUR/USD', 'PLN/UAH'])

function fxSource(code: string): string {
  const key = code.toLowerCase()
  if (key === 'nbu') return t('fx.nbu')
  if (key === 'ecb') return t('fx.ecb')
  if (key === 'nbp') return t('fx.nbp')
  return code
}

function latestFiat(rates: FxRateOut[]): FxRateOut[] {
  const byPair = new Map<string, FxRateOut>()
  for (const row of rates) {
    if (!FIAT.has(row.base_code) || !FIAT.has(row.quote_code)) continue
    const key = `${row.base_code}/${row.quote_code}`
    if (!CORE_FIAT.has(key)) continue
    const prev = byPair.get(key)
    if (!prev || row.rate_date > prev.rate_date) byPair.set(key, row)
  }
  return [...byPair.values()].sort((a, b) => {
    if (a.rate_date !== b.rate_date) return a.rate_date < b.rate_date ? 1 : -1
    return `${a.base_code}${a.quote_code}`.localeCompare(`${b.base_code}${b.quote_code}`)
  })
}

function historyValues(points: HistoryPointOut[] | undefined, field: 'mid' | 'buy' | 'sell') {
  return (points ?? [])
    .map((row) => {
      const raw = row[field]
      const value = raw == null ? NaN : Number(raw)
      return { at: row.at, value }
    })
    .filter((row) => Number.isFinite(row.value))
}

function formatSpot(value: string): string {
  const amount = Number(value)
  if (!Number.isFinite(amount)) return '—'
  const digits = amount >= 100 ? 2 : 4
  return new Intl.NumberFormat(intlLocale(), {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(amount)
}

function ageLabel(iso: string): string | null {
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return null
  const sec = Math.max(0, Math.round((Date.now() - at.getTime()) / 1000))
  if (sec < 60) return t('market.agoSec', { n: sec })
  const min = Math.round(sec / 60)
  return t('market.agoMin', { n: min })
}

const PERIODS = [
  { id: '1d', key: 'period.1d', days: 1, bucket: 'hour' as const },
  { id: '7d', key: 'period.7d', days: 7, bucket: 'hour' as const },
  { id: '30d', key: 'period.30days', days: 30, bucket: 'day' as const },
] as const
const DEFAULT_PERIOD = { id: '7d', key: 'period.7d', days: 7, bucket: 'hour' } as const

export function Market() {
  const { t } = useLocale()
  const [periodId, setPeriodId] = useState<(typeof PERIODS)[number]['id']>('7d')
  const period = PERIODS.find((row) => row.id === periodId) ?? DEFAULT_PERIOD
  const fromAt = new Date(Date.now() - period.days * 24 * 60 * 60 * 1000).toISOString()
  const spot = useQuery({
    queryKey: ['market', 'spot'],
    queryFn: getMarketSpot,
    refetchInterval: 20_000,
  })
  const fx = useQuery({ queryKey: ['fx', 'rates', 'market'], queryFn: () => getFxRates() })
  const dash = useQuery({
    queryKey: ['dashboard', 'today', 'market'],
    queryFn: () => getDashboard({ period: 'today' }),
  })
  const pool = useQuery({ queryKey: ['inventory', 'positions'], queryFn: getInventoryPositions })
  const spotHist = useQuery({
    queryKey: ['market', 'history', 'spot', period.id],
    queryFn: () => getMarketHistory({ kind: 'spot', pair: 'BTC/USDT', fromAt, bucket: period.bucket }),
  })
  const fxHist = useQuery({
    queryKey: ['market', 'history', 'fx', period.id],
    queryFn: () => getMarketHistory({ kind: 'fx', pair: 'USD/PLN', fromAt, bucket: 'day' }),
  })
  const ourHist = useQuery({
    queryKey: ['market', 'history', 'our_p2p', period.id],
    queryFn: () => getMarketHistory({ kind: 'our_p2p', pair: 'USDT/PLN', fromAt, bucket: period.bucket }),
  })

  const quotes = spot.data?.quotes ?? []
  const stale = Boolean(spot.data?.stale || quotes.some((row) => row.stale))
  const spotError = spot.data?.error || (spot.isError ? t('market.noEx') : null)

  return (
    <div className="mx-auto max-w-lg px-4 py-5 pb-8">
      <h1 className="text-xl font-semibold text-slate-100">{t('market.title')}</h1>
      <p className="mt-1 text-sm text-slate-400">{t('market.lead')}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {PERIODS.map((row) => (
          <button
            key={row.id}
            type="button"
            className={`mma-life-chip${row.id === period.id ? ' mma-life-chip-on' : ''}`}
            onClick={() => setPeriodId(row.id)}
          >
            {t(row.key)}
          </button>
        ))}
      </div>

      <section className="mt-5 rounded-lg border border-surface-border bg-surface-raised px-4 py-3">
        <div className="text-[11px] uppercase tracking-wide text-slate-500">{t('market.spotNow')}</div>
        <div className="text-[11px] text-slate-500">{t('market.spotNotP2p')}</div>
        {stale && quotes.length > 0 && (
          <p className="mma-stale mt-2">{t('market.stale')}{spotError ? ` · ${spotError}` : ''}</p>
        )}
        {spot.isPending && <p className="mt-2 text-sm text-slate-400">{t('market.ticker')}</p>}
        {!spot.isPending && quotes.length === 0 && (
          <p className="mt-2 text-sm text-slate-400">{spotError || t('market.noEx')}</p>
        )}
        {quotes.map((row) => (
          <SpotRow key={row.pair} row={row} />
        ))}
        <Sparkline
          label={t('market.spotChart', { period: t(period.key) })}
          empty={t('market.spotEmpty')}
          points={historyValues(spotHist.data?.points, 'mid')}
        />
      </section>

      <section className="mt-4 rounded-lg border border-surface-border bg-surface-raised px-4 py-3">
        <div className="text-[11px] uppercase tracking-wide text-slate-500">{t('market.fiatOff')}</div>
        <div className="text-[11px] text-slate-500">{t('market.fiatNotP2p')}</div>
        <FiatBlock rates={fx.data ?? []} loading={fx.isPending} />
        <Sparkline
          label={t('market.fxChart', { period: t(period.key) })}
          empty={t('market.fxEmpty')}
          points={historyValues(fxHist.data?.points, 'mid')}
        />
      </section>

      <section className="mt-4 rounded-lg border border-surface-border bg-surface-raised px-4 py-3">
        <div className="text-[11px] uppercase tracking-wide text-slate-500">{t('market.our')}</div>
        <div className="text-[11px] text-slate-500">{t('market.ourDeals')}</div>
        <OurP2P data={dash.data} positions={pool.data ?? []} loading={dash.isPending} />
        <Sparkline
          label={t('market.ourChart', { period: t(period.key) })}
          empty={t('market.ourEmpty')}
          points={historyValues(ourHist.data?.points, 'buy')}
        />
      </section>
    </div>
  )
}

function SpotRow({ row }: { row: SpotQuoteOut }) {
  return (
    <div className="mt-3 border-t border-surface-border pt-3 first:mt-2 first:border-0 first:pt-0">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm font-medium text-slate-100">{row.pair}</span>
        <span className="num text-lg text-slate-50">{formatSpot(row.last)}</span>
      </div>
      <p className="mt-1 text-xs text-slate-400">
        mid {formatSpot(row.mid)} · bid {formatSpot(row.bid)} · ask {formatSpot(row.ask)}
      </p>
      <p className="mt-1 text-xs text-slate-500">
        {t('market.vol24', { value: formatMoney(row.volume_24h), source: row.source })}
        {ageLabel(row.fetched_at) ? ` · ${ageLabel(row.fetched_at)}` : ''}
      </p>
    </div>
  )
}

function FiatBlock({ rates, loading }: { rates: FxRateOut[]; loading: boolean }) {
  const rows = latestFiat(rates)
  if (loading) return <p className="mt-2 text-sm text-slate-400">{t('market.fxLoad')}</p>
  if (rows.length === 0) {
    return <p className="mt-2 text-sm text-slate-400">{t('market.fxNone')}</p>
  }
  return (
    <ul className="mt-2 space-y-1.5">
      {rows.map((row) => (
        <li key={`${row.base_code}-${row.quote_code}-${row.source}`} className="flex justify-between gap-3 text-sm">
          <span className="text-slate-200">
            {row.base_code}/{row.quote_code}
            <span className="ml-2 text-[11px] text-slate-500">
              {fxSource(row.source)} · {row.rate_date}
            </span>
          </span>
          <span className="num text-slate-100">{formatRate(row.rate)}</span>
        </li>
      ))}
    </ul>
  )
}

function OurP2P({
  data,
  positions,
  loading,
}: {
  data: DashboardOut | undefined
  positions: PositionOut[]
  loading: boolean
}) {
  if (loading) return <p className="mt-2 text-sm text-slate-400">{t('market.ourLoad')}</p>
  if (!data || data.orders === 0) {
    return <p className="mt-2 text-sm text-slate-400">{t('market.noToday')}</p>
  }
  const asset = data.asset_code || 'USDT'
  const lastBuy = data.last_buy_price
    ? t('market.lastBuy', { rate: formatRate(data.last_buy_price), pair: `${asset}/${data.last_buy_fiat || data.primary_code}` })
    : null
  const lastSell = data.last_sell_price
    ? t('market.lastSell', { rate: formatRate(data.last_sell_price), pair: `${asset}/${data.last_sell_fiat || data.primary_code}` })
    : null
  const vwap =
    data.vwap_buy || data.vwap_sell
      ? t('market.avg', { buy: formatRate(data.vwap_buy), sell: formatRate(data.vwap_sell) })
      : null
  const spread = data.spread_pct != null ? t('market.spread', { pct: formatRate(data.spread_pct) }) : null
  const live = positions.filter((row) => Number(row.quantity_remaining) > 0)

  return (
    <div className="mt-2 space-y-1 text-sm text-slate-200">
      <p>{[lastBuy, lastSell].filter(Boolean).join(' · ') || t('market.noLast')}</p>
      {(vwap || spread) && <p className="text-xs text-slate-400">{[vwap, spread].filter(Boolean).join(' · ')}</p>}
      <p className="text-xs text-slate-500">
        {t('market.pool')}{' '}
        {live.length === 0
          ? t('market.poolEmpty')
          : live
              .map((row) => `${formatQty(row.quantity_remaining)} ${row.asset_code}`)
              .join(' · ')}
      </p>
    </div>
  )
}
